import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { JOB_CATEGORIES, JobCategory } from '@/lib/types/job';
import { differenceInDays, differenceInHours, subDays } from 'date-fns';

export interface VendorKPIs {
  activeVendors: number;
  newVendors: number;
  activeVendorsTrend: number;
  avgBidsPerJob: number;
  totalBids: number;
  avgBidsPerJobTrend: number;
  avgResponseTimeHours: number;
  medianResponseTimeHours: number;
  avgResponseTimeTrend: number;
  diversitySpendPercent: number;
  diversitySpendAmount: number;
  diversitySpendTrend: number;
}

export interface VendorPerformanceRow {
  vendorId: string;
  vendorName: string;
  certifications: string[];
  bidsSubmitted: number;
  bidResponseRate: number | null;
  avgResponseTimeHours: number;
  winRate: number;
  jobsCompleted: number;
  totalValueCents: number;
  totalValue: number;
  avgJobValue: number;
}

export interface VendorDistributionData {
  category: JobCategory;
  label: string;
  biddingVendors: number;
  winningVendors: number;
  totalVendors: number;
}

export interface TopVendorData {
  vendorId: string;
  vendorName: string;
  value: number;
  certifications: string[];
}

export type TopVendorMetric = 'value' | 'winRate' | 'responseTime';

function getCategoryLabel(category: string): string {
  const found = JOB_CATEGORIES.find(c => c.value === category);
  return found?.label || 'Other';
}

function calculateMedian(numbers: number[]): number {
  if (numbers.length === 0) return 0;
  const sorted = [...numbers].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

export function useVendorKPIs(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined,
  categories: JobCategory[]
) {
  return useQuery({
    queryKey: ['vendor-kpis', startDate.toISOString(), endDate.toISOString(), organizationId, categories],
    queryFn: async (): Promise<VendorKPIs> => {
      if (!organizationId) {
        return {
          activeVendors: 0, newVendors: 0, activeVendorsTrend: 0,
          avgBidsPerJob: 0, totalBids: 0, avgBidsPerJobTrend: 0,
          avgResponseTimeHours: 0, medianResponseTimeHours: 0, avgResponseTimeTrend: 0,
          diversitySpendPercent: 0, diversitySpendAmount: 0, diversitySpendTrend: 0,
        };
      }

      // Fetch bids with RFQ and job info for current period
      const { data: bidsData, error: bidsError } = await supabase
        .from('bids')
        .select(`
          id,
          vendor_user_id,
          created_at,
          rfq:rfqs!inner(
            id,
            opens_at,
            job:jobs!inner(
              id,
              organization_id,
              category
            )
          )
        `)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (bidsError) throw bidsError;

      // Filter by organization and categories
      const filteredBids = (bidsData || []).filter(bid => {
        const job = bid.rfq?.job;
        if (!job || job.organization_id !== organizationId) return false;
        if (categories.length > 0 && !categories.includes(job.category as JobCategory)) return false;
        return true;
      });

      // Fetch awards for current period
      const { data: awardsData, error: awardsError } = await supabase
        .from('awards')
        .select(`
          id,
          winning_bid_id,
          total_award_amount_cents,
          job:jobs!inner(
            id,
            organization_id,
            category
          )
        `)
        .gte('awarded_at', startDate.toISOString())
        .lte('awarded_at', endDate.toISOString());

      if (awardsError) throw awardsError;

      const filteredAwards = (awardsData || []).filter(award => {
        const job = award.job;
        if (!job || job.organization_id !== organizationId) return false;
        if (categories.length > 0 && !categories.includes(job.category as JobCategory)) return false;
        return true;
      });

      // Get vendor user IDs from bids
      const vendorUserIdsFromBids = new Set(filteredBids.map(b => b.vendor_user_id));
      
      // Get vendor user IDs from awards (via winning bids)
      const winningBidIds = filteredAwards.map(a => a.winning_bid_id).filter(Boolean);
      let vendorUserIdsFromAwards = new Set<string>();
      if (winningBidIds.length > 0) {
        const { data: winningBids } = await supabase
          .from('bids')
          .select('vendor_user_id')
          .in('id', winningBidIds);
        vendorUserIdsFromAwards = new Set((winningBids || []).map(b => b.vendor_user_id));
      }

      // Active vendors = those who bid OR won
      const activeVendorIds = new Set([...vendorUserIdsFromBids, ...vendorUserIdsFromAwards]);
      const activeVendors = activeVendorIds.size;

      // Count RFQs in period for this org
      const rfqIds = new Set(filteredBids.map(b => b.rfq?.id).filter(Boolean));
      const totalBids = filteredBids.length;
      const avgBidsPerJob = rfqIds.size > 0 ? totalBids / rfqIds.size : 0;

      // Calculate response times
      const responseTimes: number[] = [];
      for (const bid of filteredBids) {
        if (bid.rfq?.opens_at && bid.created_at) {
          const hours = differenceInHours(new Date(bid.created_at), new Date(bid.rfq.opens_at));
          if (hours >= 0) responseTimes.push(hours);
        }
      }
      const avgResponseTimeHours = responseTimes.length > 0 
        ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length 
        : 0;
      const medianResponseTimeHours = calculateMedian(responseTimes);

      // Calculate diversity spend
      // First, get vendor organizations from bids
      const vendorUserIdArray = Array.from(activeVendorIds);
      let vendorProfileIdMap = new Map<string, string>();
      let certifiedVendorProfileIds = new Set<string>();

      if (vendorUserIdArray.length > 0) {
        // Get vendor profiles to map user_id to profile id
        const { data: vendorProfiles } = await supabase
          .from('vendor_profiles_enhanced')
          .select('id, user_id')
          .in('user_id', vendorUserIdArray);
        
        for (const profile of vendorProfiles || []) {
          vendorProfileIdMap.set(profile.user_id, profile.id);
        }

        // Get certifications for vendor profiles
        const vendorProfileIds = Array.from(new Set(vendorProfileIdMap.values()));
        if (vendorProfileIds.length > 0) {
          const { data: certifications } = await supabase
            .from('vendor_certifications')
            .select('vendor_profile_id')
            .in('vendor_profile_id', vendorProfileIds);
          
          certifiedVendorProfileIds = new Set((certifications || []).map(c => c.vendor_profile_id));
        }
      }

      // Calculate diversity spend from awards
      let totalSpend = 0;
      let diversitySpend = 0;
      for (const award of filteredAwards) {
        const amount = award.total_award_amount_cents || 0;
        totalSpend += amount;
        
        // Check if winning vendor is certified
        if (award.winning_bid_id && winningBidIds.includes(award.winning_bid_id)) {
          const { data: winningBid } = await supabase
            .from('bids')
            .select('vendor_user_id')
            .eq('id', award.winning_bid_id)
            .single();
          
          if (winningBid) {
            const vendorProfileId = vendorProfileIdMap.get(winningBid.vendor_user_id);
            if (vendorProfileId && certifiedVendorProfileIds.has(vendorProfileId)) {
              diversitySpend += amount;
            }
          }
        }
      }

      const diversitySpendPercent = totalSpend > 0 ? (diversitySpend / totalSpend) * 100 : 0;
      const diversitySpendAmount = diversitySpend / 100;

      // Calculate previous period for trends
      const periodDays = differenceInDays(endDate, startDate);
      const prevEndDate = subDays(startDate, 1);
      const prevStartDate = subDays(prevEndDate, periodDays);

      // Previous period bids for trend calculation
      const { data: prevBidsData } = await supabase
        .from('bids')
        .select(`
          vendor_user_id,
          created_at,
          rfq:rfqs!inner(
            id,
            opens_at,
            job:jobs!inner(organization_id, category)
          )
        `)
        .gte('created_at', prevStartDate.toISOString())
        .lte('created_at', prevEndDate.toISOString());

      const prevFilteredBids = (prevBidsData || []).filter(bid => {
        const job = bid.rfq?.job;
        if (!job || job.organization_id !== organizationId) return false;
        if (categories.length > 0 && !categories.includes(job.category as JobCategory)) return false;
        return true;
      });

      const prevVendorIds = new Set(prevFilteredBids.map(b => b.vendor_user_id));
      const prevRfqIds = new Set(prevFilteredBids.map(b => b.rfq?.id).filter(Boolean));
      const prevAvgBids = prevRfqIds.size > 0 ? prevFilteredBids.length / prevRfqIds.size : 0;

      const prevResponseTimes: number[] = [];
      for (const bid of prevFilteredBids) {
        if (bid.rfq?.opens_at && bid.created_at) {
          const hours = differenceInHours(new Date(bid.created_at), new Date(bid.rfq.opens_at));
          if (hours >= 0) prevResponseTimes.push(hours);
        }
      }
      const prevAvgResponseTime = prevResponseTimes.length > 0
        ? prevResponseTimes.reduce((a, b) => a + b, 0) / prevResponseTimes.length
        : 0;

      // Calculate trends (percentage change)
      const activeVendorsTrend = prevVendorIds.size > 0 
        ? ((activeVendors - prevVendorIds.size) / prevVendorIds.size) * 100 
        : 0;
      const avgBidsPerJobTrend = prevAvgBids > 0 
        ? ((avgBidsPerJob - prevAvgBids) / prevAvgBids) * 100 
        : 0;
      const avgResponseTimeTrend = prevAvgResponseTime > 0 
        ? ((avgResponseTimeHours - prevAvgResponseTime) / prevAvgResponseTime) * 100 
        : 0;

      // New vendors = those who bid for first time in this period
      const newVendors = [...activeVendorIds].filter(id => !prevVendorIds.has(id)).length;

      return {
        activeVendors,
        newVendors,
        activeVendorsTrend,
        avgBidsPerJob,
        totalBids,
        avgBidsPerJobTrend,
        avgResponseTimeHours,
        medianResponseTimeHours,
        avgResponseTimeTrend,
        diversitySpendPercent,
        diversitySpendAmount,
        diversitySpendTrend: 0, // Would need previous period awards to calculate
      };
    },
    enabled: !!organizationId,
  });
}

export function useVendorPerformanceTable(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined,
  categories: JobCategory[]
) {
  return useQuery({
    queryKey: ['vendor-performance-table', startDate.toISOString(), endDate.toISOString(), organizationId, categories],
    queryFn: async (): Promise<VendorPerformanceRow[]> => {
      if (!organizationId) return [];

      // Fetch bids with RFQ and job info
      const { data: bidsData, error: bidsError } = await supabase
        .from('bids')
        .select(`
          id,
          vendor_user_id,
          created_at,
          rfq:rfqs!inner(
            id,
            opens_at,
            job:jobs!inner(
              id,
              organization_id,
              category
            )
          )
        `)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (bidsError) throw bidsError;

      // Filter by organization and categories
      const filteredBids = (bidsData || []).filter(bid => {
        const job = bid.rfq?.job;
        if (!job || job.organization_id !== organizationId) return false;
        if (categories.length > 0 && !categories.includes(job.category as JobCategory)) return false;
        return true;
      });

      // Fetch awards
      const { data: awardsData, error: awardsError } = await supabase
        .from('awards')
        .select(`
          id,
          winning_bid_id,
          total_award_amount_cents,
          job:jobs!inner(
            id,
            organization_id,
            category
          )
        `)
        .gte('awarded_at', startDate.toISOString())
        .lte('awarded_at', endDate.toISOString());

      if (awardsError) throw awardsError;

      const filteredAwards = (awardsData || []).filter(award => {
        const job = award.job;
        if (!job || job.organization_id !== organizationId) return false;
        if (categories.length > 0 && !categories.includes(job.category as JobCategory)) return false;
        return true;
      });

      // Map winning bids to vendor user IDs
      const winningBidIds = filteredAwards.map(a => a.winning_bid_id).filter(Boolean);
      const winningBidToAward = new Map<string, { amount: number }>();
      for (const award of filteredAwards) {
        if (award.winning_bid_id) {
          winningBidToAward.set(award.winning_bid_id, { amount: award.total_award_amount_cents || 0 });
        }
      }

      // Get winning bid vendor user IDs
      let winningBidVendorMap = new Map<string, string>();
      if (winningBidIds.length > 0) {
        const { data: winningBids } = await supabase
          .from('bids')
          .select('id, vendor_user_id')
          .in('id', winningBidIds);
        for (const bid of winningBids || []) {
          winningBidVendorMap.set(bid.id, bid.vendor_user_id);
        }
      }

      // Aggregate by vendor
      const vendorStats = new Map<string, {
        bidsSubmitted: number;
        responseTimes: number[];
        wins: number;
        totalValueCents: number;
      }>();

      for (const bid of filteredBids) {
        const vendorId = bid.vendor_user_id;
        const existing = vendorStats.get(vendorId) || {
          bidsSubmitted: 0,
          responseTimes: [],
          wins: 0,
          totalValueCents: 0,
        };

        existing.bidsSubmitted++;

        if (bid.rfq?.opens_at && bid.created_at) {
          const hours = differenceInHours(new Date(bid.created_at), new Date(bid.rfq.opens_at));
          if (hours >= 0) existing.responseTimes.push(hours);
        }

        // Check if this bid won
        if (winningBidToAward.has(bid.id)) {
          existing.wins++;
          existing.totalValueCents += winningBidToAward.get(bid.id)!.amount;
        }

        vendorStats.set(vendorId, existing);
      }

      // Also add vendors who won but might have bid in previous period
      for (const [bidId, vendorUserId] of winningBidVendorMap) {
        if (!vendorStats.has(vendorUserId)) {
          const award = winningBidToAward.get(bidId);
          vendorStats.set(vendorUserId, {
            bidsSubmitted: 0,
            responseTimes: [],
            wins: 1,
            totalValueCents: award?.amount || 0,
          });
        }
      }

      // Get vendor names and certifications
      const vendorUserIds = Array.from(vendorStats.keys());
      const vendorNamesMap = new Map<string, string>();
      const vendorProfileMap = new Map<string, string>();
      const vendorCertsMap = new Map<string, string[]>();

      if (vendorUserIds.length > 0) {
        const { data: profiles } = await supabase
          .from('vendor_profiles_enhanced')
          .select('id, user_id, business_name')
          .in('user_id', vendorUserIds);

        for (const profile of profiles || []) {
          vendorNamesMap.set(profile.user_id, profile.business_name || 'Unknown Vendor');
          vendorProfileMap.set(profile.user_id, profile.id);
        }

        // Get certifications
        const vendorProfileIds = Array.from(new Set(vendorProfileMap.values()));
        if (vendorProfileIds.length > 0) {
          const { data: certs } = await supabase
            .from('vendor_certifications')
            .select(`
              vendor_profile_id,
              certification_type:certification_types(name)
            `)
            .in('vendor_profile_id', vendorProfileIds);

          const profileCertsMap = new Map<string, string[]>();
          for (const cert of certs || []) {
            const certName = (cert.certification_type as any)?.name;
            if (certName) {
              const existing = profileCertsMap.get(cert.vendor_profile_id) || [];
              if (!existing.includes(certName)) {
                existing.push(certName);
              }
              profileCertsMap.set(cert.vendor_profile_id, existing);
            }
          }

          for (const [userId, profileId] of vendorProfileMap) {
            vendorCertsMap.set(userId, profileCertsMap.get(profileId) || []);
          }
        }
      }

      // Build result rows
      const result: VendorPerformanceRow[] = [];
      for (const [vendorId, stats] of vendorStats) {
        const avgResponseTimeHours = stats.responseTimes.length > 0
          ? stats.responseTimes.reduce((a, b) => a + b, 0) / stats.responseTimes.length
          : 0;
        const winRate = stats.bidsSubmitted > 0 ? (stats.wins / stats.bidsSubmitted) * 100 : 0;

        result.push({
          vendorId,
          vendorName: vendorNamesMap.get(vendorId) || 'Unknown Vendor',
          certifications: vendorCertsMap.get(vendorId) || [],
          bidsSubmitted: stats.bidsSubmitted,
          bidResponseRate: null, // Would need invitation data
          avgResponseTimeHours,
          winRate,
          jobsCompleted: stats.wins,
          totalValueCents: stats.totalValueCents,
          totalValue: stats.totalValueCents / 100,
          avgJobValue: stats.wins > 0 ? (stats.totalValueCents / 100) / stats.wins : 0,
        });
      }

      // Sort by total value descending
      result.sort((a, b) => b.totalValueCents - a.totalValueCents);

      return result;
    },
    enabled: !!organizationId,
  });
}

export function useVendorDistribution(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined,
  categories: JobCategory[]
) {
  return useQuery({
    queryKey: ['vendor-distribution', startDate.toISOString(), endDate.toISOString(), organizationId, categories],
    queryFn: async (): Promise<VendorDistributionData[]> => {
      if (!organizationId) return [];

      // Fetch bids with job categories
      const { data: bidsData, error: bidsError } = await supabase
        .from('bids')
        .select(`
          vendor_user_id,
          rfq:rfqs!inner(
            job:jobs!inner(
              organization_id,
              category
            )
          )
        `)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (bidsError) throw bidsError;

      // Fetch awards with job categories
      const { data: awardsData, error: awardsError } = await supabase
        .from('awards')
        .select(`
          winning_bid_id,
          job:jobs!inner(
            organization_id,
            category
          )
        `)
        .gte('awarded_at', startDate.toISOString())
        .lte('awarded_at', endDate.toISOString());

      if (awardsError) throw awardsError;

      // Filter bids by organization
      const filteredBids = (bidsData || []).filter(bid => {
        const job = bid.rfq?.job;
        return job && job.organization_id === organizationId;
      });

      // Filter awards by organization
      const filteredAwards = (awardsData || []).filter(award => {
        const job = award.job;
        return job && job.organization_id === organizationId;
      });

      // Get winning bid vendor user IDs
      const winningBidIds = filteredAwards.map(a => a.winning_bid_id).filter(Boolean);
      const winningBidVendorMap = new Map<string, string>();
      if (winningBidIds.length > 0) {
        const { data: winningBids } = await supabase
          .from('bids')
          .select('id, vendor_user_id')
          .in('id', winningBidIds);
        for (const bid of winningBids || []) {
          winningBidVendorMap.set(bid.id, bid.vendor_user_id);
        }
      }

      // Build distribution by category
      const categoryData = new Map<string, { biddingVendors: Set<string>; winningVendors: Set<string> }>();

      for (const bid of filteredBids) {
        const category = bid.rfq?.job?.category || 'other';
        const existing = categoryData.get(category) || { biddingVendors: new Set(), winningVendors: new Set() };
        existing.biddingVendors.add(bid.vendor_user_id);
        categoryData.set(category, existing);
      }

      for (const award of filteredAwards) {
        const category = award.job?.category || 'other';
        const vendorUserId = award.winning_bid_id ? winningBidVendorMap.get(award.winning_bid_id) : null;
        if (vendorUserId) {
          const existing = categoryData.get(category) || { biddingVendors: new Set(), winningVendors: new Set() };
          existing.winningVendors.add(vendorUserId);
          categoryData.set(category, existing);
        }
      }

      // Convert to result array
      const result: VendorDistributionData[] = JOB_CATEGORIES
        .filter(cat => categories.length === 0 || categories.includes(cat.value))
        .map(cat => {
          const data = categoryData.get(cat.value) || { biddingVendors: new Set(), winningVendors: new Set() };
          return {
            category: cat.value,
            label: cat.label,
            biddingVendors: data.biddingVendors.size,
            winningVendors: data.winningVendors.size,
            totalVendors: data.biddingVendors.size,
          };
        });

      return result;
    },
    enabled: !!organizationId,
  });
}

export function useTopPerformingVendors(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined,
  categories: JobCategory[],
  metric: TopVendorMetric
) {
  return useQuery({
    queryKey: ['top-performing-vendors', startDate.toISOString(), endDate.toISOString(), organizationId, categories, metric],
    queryFn: async (): Promise<TopVendorData[]> => {
      if (!organizationId) return [];

      // Reuse the performance table data
      const { data: bidsData } = await supabase
        .from('bids')
        .select(`
          id,
          vendor_user_id,
          created_at,
          rfq:rfqs!inner(
            opens_at,
            job:jobs!inner(
              organization_id,
              category
            )
          )
        `)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      const filteredBids = (bidsData || []).filter(bid => {
        const job = bid.rfq?.job;
        if (!job || job.organization_id !== organizationId) return false;
        if (categories.length > 0 && !categories.includes(job.category as JobCategory)) return false;
        return true;
      });

      const { data: awardsData } = await supabase
        .from('awards')
        .select(`
          winning_bid_id,
          total_award_amount_cents,
          job:jobs!inner(
            organization_id,
            category
          )
        `)
        .gte('awarded_at', startDate.toISOString())
        .lte('awarded_at', endDate.toISOString());

      const filteredAwards = (awardsData || []).filter(award => {
        const job = award.job;
        if (!job || job.organization_id !== organizationId) return false;
        if (categories.length > 0 && !categories.includes(job.category as JobCategory)) return false;
        return true;
      });

      const winningBidIds = filteredAwards.map(a => a.winning_bid_id).filter(Boolean);
      const winningBidToAward = new Map<string, number>();
      for (const award of filteredAwards) {
        if (award.winning_bid_id) {
          winningBidToAward.set(award.winning_bid_id, award.total_award_amount_cents || 0);
        }
      }

      // Aggregate vendor stats
      const vendorStats = new Map<string, {
        bids: number;
        wins: number;
        totalValue: number;
        responseTimes: number[];
      }>();

      for (const bid of filteredBids) {
        const vendorId = bid.vendor_user_id;
        const existing = vendorStats.get(vendorId) || { bids: 0, wins: 0, totalValue: 0, responseTimes: [] };
        
        existing.bids++;
        if (bid.rfq?.opens_at && bid.created_at) {
          const hours = differenceInHours(new Date(bid.created_at), new Date(bid.rfq.opens_at));
          if (hours >= 0) existing.responseTimes.push(hours);
        }
        if (winningBidToAward.has(bid.id)) {
          existing.wins++;
          existing.totalValue += winningBidToAward.get(bid.id)!;
        }

        vendorStats.set(vendorId, existing);
      }

      // Get vendor names and certifications
      const vendorUserIds = Array.from(vendorStats.keys());
      const vendorNamesMap = new Map<string, string>();
      const vendorProfileMap = new Map<string, string>();
      const vendorCertsMap = new Map<string, string[]>();

      if (vendorUserIds.length > 0) {
        const { data: profiles } = await supabase
          .from('vendor_profiles_enhanced')
          .select('id, user_id, business_name')
          .in('user_id', vendorUserIds);

        for (const profile of profiles || []) {
          vendorNamesMap.set(profile.user_id, profile.business_name || 'Unknown');
          vendorProfileMap.set(profile.user_id, profile.id);
        }

        const vendorProfileIds = Array.from(new Set(vendorProfileMap.values()));
        if (vendorProfileIds.length > 0) {
          const { data: certs } = await supabase
            .from('vendor_certifications')
            .select('vendor_profile_id, certification_type:certification_types(name)')
            .in('vendor_profile_id', vendorProfileIds);

          const profileCertsMap = new Map<string, string[]>();
          for (const cert of certs || []) {
            const certName = (cert.certification_type as any)?.name;
            if (certName) {
              const existing = profileCertsMap.get(cert.vendor_profile_id) || [];
              if (!existing.includes(certName)) existing.push(certName);
              profileCertsMap.set(cert.vendor_profile_id, existing);
            }
          }

          for (const [userId, profileId] of vendorProfileMap) {
            vendorCertsMap.set(userId, profileCertsMap.get(profileId) || []);
          }
        }
      }

      // Sort by selected metric and take top 10
      let result: TopVendorData[] = [];

      if (metric === 'value') {
        result = Array.from(vendorStats.entries())
          .map(([vendorId, stats]) => ({
            vendorId,
            vendorName: vendorNamesMap.get(vendorId) || 'Unknown',
            value: stats.totalValue / 100,
            certifications: vendorCertsMap.get(vendorId) || [],
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 10);
      } else if (metric === 'winRate') {
        result = Array.from(vendorStats.entries())
          .filter(([_, stats]) => stats.bids >= 3) // Min 3 bids to qualify
          .map(([vendorId, stats]) => ({
            vendorId,
            vendorName: vendorNamesMap.get(vendorId) || 'Unknown',
            value: stats.bids > 0 ? (stats.wins / stats.bids) * 100 : 0,
            certifications: vendorCertsMap.get(vendorId) || [],
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 10);
      } else if (metric === 'responseTime') {
        result = Array.from(vendorStats.entries())
          .filter(([_, stats]) => stats.responseTimes.length > 0)
          .map(([vendorId, stats]) => ({
            vendorId,
            vendorName: vendorNamesMap.get(vendorId) || 'Unknown',
            value: stats.responseTimes.reduce((a, b) => a + b, 0) / stats.responseTimes.length,
            certifications: vendorCertsMap.get(vendorId) || [],
          }))
          .sort((a, b) => a.value - b.value) // Lower is better
          .slice(0, 10);
      }

      return result;
    },
    enabled: !!organizationId,
  });
}

export function useVendorExportData(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined,
  categories: JobCategory[]
) {
  const tableQuery = useVendorPerformanceTable(startDate, endDate, organizationId, categories);
  
  return {
    data: tableQuery.data,
    isLoading: tableQuery.isLoading,
    error: tableQuery.error,
  };
}
